import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./Dashboard.css";
import { apiFetch } from "../utils/apifetch";
import { useAuth } from "../App";   // ✅ FIX: correct context source

export default function Dashboard() {
  const navigate = useNavigate();
  const { logout } = useAuth();

  const isMountedRef = useRef(true);

  const [stats, setStats] = useState({
    profiles: { total: 0, active: 0, inactive: 0 },
    articles: { total: 0 },
    judgments: { total: 0 },
    judges: { total: 0 },
    acts: { total: 0 },
    users: { total: 0, active: 0, inactive: 0 },
  });

  const [error, setError] = useState("");

  useEffect(() => {
    isMountedRef.current = true;

    const loadDashboard = async () => {
      try {
        const res = await apiFetch(
          "/dashboard/summary",
          {},
          logout,
          navigate
        );

        const data = await res.json();

        // ✅ Guard against unmount
        if (!isMountedRef.current) return;

        setStats((prev) => ({
          profiles: data.profiles ?? prev.profiles,
          articles: data.articles ?? prev.articles,
          judgments: data.judgments ?? prev.judgments,
          judges: data.judges ?? prev.judges,
          acts: data.acts ?? prev.acts,
          users: data.users ?? prev.users,
        }));
      } catch (err) {
        if (!isMountedRef.current) return;
        console.error("Dashboard error:", err);
        setError("Unable to load dashboard data.");
      }
    };

    loadDashboard();

    return () => {
      // ✅ Mark unmounted
      isMountedRef.current = false;
    };
  }, [logout, navigate]); // ✅ Correct dependencies

  if (error) {
    return (
      <section className="dashboard">
        <h2 style={{ color: "#b91c1c" }}>{error}</h2>
      </section>
    );
  }

  return (
    <section className="dashboard">
      <header className="dashboard-header">
        <h1 className="dashboard-title">Dashboard</h1>
        <p className="dashboard-subtitle">Overview of system records</p>
      </header>

      <div className="dashboard-grid">
        <DashboardCard
          title="Profiles"
          variant="profiles"
          stats={stats.profiles}
          onClick={() => navigate("/profile")}
        />

        <DashboardCard
          title="Articles"
          variant="articles"
          stats={stats.articles}
          onClick={() => navigate("/article")}
        />

        <DashboardCard
          title="Judgments"
          variant="judgements"
          stats={stats.judgments}
          onClick={() => navigate("/judgement")}
        />

        <DashboardCard
          title="Judges"
          variant="judges"
          stats={stats.judges}
          onClick={() => navigate("/judge")}
        />

        <DashboardCard
          title="Acts"
          variant="acts"
          stats={stats.acts}
          onClick={() => navigate("/act")}
        />

        <DashboardCard
          title="Users"
          variant="users"
          stats={stats.users}
          onClick={() => navigate("/user")}
        />
      </div>
    </section>
  );
}

/* =========================================================
   DASHBOARD CARD
========================================================= */
function DashboardCard({ title, stats = {}, onClick, variant }) {
  return (
    <div
      className={`dashboard-card ${variant}`}
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => e.key === "Enter" && onClick()}
    >
      <h3 className="card-title">{title}</h3>

      <div className="card-stats">
        {"total" in stats && (
          <div>
            <span className="label">Total</span>
            <span className="value">{stats.total}</span>
          </div>
        )}

        {"active" in stats && (
          <div>
            <span className="label">Active</span>
            <span className="value active">{stats.active}</span>
          </div>
        )}

        {"inactive" in stats && (
          <div>
            <span className="label">Inactive</span>
            <span className="value inactive">{stats.inactive}</span>
          </div>
        )}
      </div>
    </div>
  );
}